import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Trophy, Timer, Wind } from 'lucide-react';

const BottleFlip = ({ onGameEnd }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('waiting');
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(15);
  const [powerLevel, setPowerLevel] = useState(0);
  const [isCharging, setIsCharging] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [streak, setStreak] = useState(0);
  const gameLoopRef = useRef(null);
  const bottleRef = useRef({
    x: 100,
    y: 400,
    angle: 0,
    velocityX: 0,
    velocityY: 0,
    angularVelocity: 0,
    isFlying: false
  });

  useEffect(() => {
    if (gameState === 'playing') {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      const gravity = 0.5;
      const groundY = height - 80;

      const gameLoop = () => {
        // Clear canvas
        const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
        bgGradient.addColorStop(0, '#87CEEB');
        bgGradient.addColorStop(1, '#E0F6FF');
        ctx.fillStyle = bgGradient;
        ctx.fillRect(0, 0, width, height);

        // Draw ground
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(0, groundY, width, height - groundY);
        ctx.fillStyle = '#90EE90';
        ctx.fillRect(0, groundY, width, 10);

        // Draw table/platform
        const platformX = width - 150;
        const platformY = groundY - 20;
        const platformWidth = 100;
        
        ctx.fillStyle = '#CD853F';
        ctx.fillRect(platformX, platformY, platformWidth, 20);
        ctx.strokeStyle = '#8B4513';
        ctx.lineWidth = 3;
        ctx.strokeRect(platformX, platformY, platformWidth, 20);

        // Draw target zone indicator
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(platformX - 5, platformY - 5, platformWidth + 10, 30);
        ctx.setLineDash([]);

        // Update bottle physics
        if (bottleRef.current.isFlying) {
          bottleRef.current.velocityY += gravity;
          bottleRef.current.x += bottleRef.current.velocityX;
          bottleRef.current.y += bottleRef.current.velocityY;
          bottleRef.current.angle += bottleRef.current.angularVelocity;

          // Check landing
          if (bottleRef.current.y >= groundY - 70) {
            bottleRef.current.isFlying = false;
            
            // Check if landed on platform
            const bottleBottom = bottleRef.current.x;
            const isOnPlatform = bottleBottom >= platformX && bottleBottom <= platformX + platformWidth;
            
            // Check if bottle is upright (angle close to 0 or 360)
            const normalizedAngle = Math.abs(bottleRef.current.angle % 360);
            const isUpright = normalizedAngle < 20 || normalizedAngle > 340;

            if (isOnPlatform && isUpright) {
              // Successful flip!
              let points = 100;
              const angleAccuracy = Math.min(20 - Math.min(normalizedAngle, 360 - normalizedAngle), 20);
              points += Math.round(angleAccuracy * 5);
              
              // Streak bonus
              setStreak(prev => prev + 1);
              points += (streak * 10);

              setScore(prev => prev + points);
              setFeedback(normalizedAngle < 5 ? '🎯 PERFECT FLIP!' : '✨ Nice Flip!');
            } else if (isOnPlatform) {
              setFeedback('😅 Landed but not upright!');
              setStreak(0);
            } else {
              setFeedback('❌ Missed the platform!');
              setStreak(0);
            }

            setTimeout(() => {
              setFeedback('');
              // Reset bottle position
              bottleRef.current = {
                x: 100,
                y: groundY - 70,
                angle: 0,
                velocityX: 0,
                velocityY: 0,
                angularVelocity: 0,
                isFlying: false
              };
            }, 1500);

            setAttempts(prev => {
              const newAttempts = prev - 1;
              if (newAttempts <= 0) {
                setTimeout(() => setGameState('finished'), 1500);
              }
              return newAttempts;
            });
          }
        }

        // Draw bottle
        ctx.save();
        ctx.translate(bottleRef.current.x, bottleRef.current.y);
        ctx.rotate((bottleRef.current.angle * Math.PI) / 180);

        // Bottle body
        const bottleGradient = ctx.createLinearGradient(-15, -35, 15, 35);
        bottleGradient.addColorStop(0, '#60a5fa');
        bottleGradient.addColorStop(0.5, '#3b82f6');
        bottleGradient.addColorStop(1, '#2563eb');
        
        ctx.fillStyle = bottleGradient;
        ctx.fillRect(-15, -35, 30, 70);
        
        // Bottle cap
        ctx.fillStyle = '#ef4444';
        ctx.fillRect(-12, -40, 24, 10);
        
        // Bottle label
        ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.fillRect(-12, -10, 24, 20);
        
        // Bottle outline
        ctx.strokeStyle = '#1e3a8a';
        ctx.lineWidth = 2;
        ctx.strokeRect(-15, -35, 30, 70);
        ctx.strokeRect(-12, -40, 24, 10);

        ctx.restore();

        // Draw power meter if charging
        if (isCharging) {
          const meterX = 50;
          const meterY = 100;
          const meterWidth = 30;
          const meterHeight = 200;
          
          // Meter background
          ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
          ctx.fillRect(meterX, meterY, meterWidth, meterHeight);
          
          // Power fill
          const powerHeight = (powerLevel / 100) * meterHeight;
          const powerGradient = ctx.createLinearGradient(
            meterX, meterY + meterHeight - powerHeight,
            meterX, meterY + meterHeight
          );
          powerGradient.addColorStop(0, '#22c55e');
          powerGradient.addColorStop(0.5, '#fbbf24');
          powerGradient.addColorStop(1, '#ef4444');
          
          ctx.fillStyle = powerGradient;
          ctx.fillRect(meterX, meterY + meterHeight - powerHeight, meterWidth, powerHeight);
          
          // Meter border
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.strokeRect(meterX, meterY, meterWidth, meterHeight);
          
          // Label
          ctx.fillStyle = '#fff';
          ctx.font = 'bold 14px Arial';
          ctx.fillText('POWER', meterX - 5, meterY - 10);
        }

        gameLoopRef.current = requestAnimationFrame(gameLoop);
      };

      gameLoop();

      return () => {
        if (gameLoopRef.current) {
          cancelAnimationFrame(gameLoopRef.current);
        }
      };
    }
  }, [gameState, powerLevel, isCharging, streak]);

  useEffect(() => {
    if (isCharging) {
      const interval = setInterval(() => {
        setPowerLevel(prev => {
          if (prev >= 100) return 0;
          return prev + 2;
        });
      }, 30);

      return () => clearInterval(interval);
    }
  }, [isCharging]);

  useEffect(() => {
    if (gameState === 'finished') {
      // Stop the game loop
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
      // Call onGameEnd after a short delay to show the final screen
      setTimeout(() => {
        onGameEnd(score);
      }, 2000);
    }
  }, [gameState, score, onGameEnd]);

  const handleFlipStart = () => {
    if (gameState === 'playing' && !bottleRef.current.isFlying && !isCharging) {
      setIsCharging(true);
      setPowerLevel(0);
    }
  };

  const handleFlipRelease = () => {
    if (gameState === 'playing' && isCharging) {
      setIsCharging(false);
      
      // Launch bottle
      const power = powerLevel / 100;
      bottleRef.current.velocityX = 5 + power * 10;
      bottleRef.current.velocityY = -15 - power * 8;
      bottleRef.current.angularVelocity = 8 + power * 8;
      bottleRef.current.isFlying = true;
    }
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setAttempts(15);
    setPowerLevel(0);
    setStreak(0);
    bottleRef.current = {
      x: 100,
      y: 400,
      angle: 0,
      velocityX: 0,
      velocityY: 0,
      angularVelocity: 0,
      isFlying: false
    };
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 rounded-lg p-6 border border-cyan-500/30">
        {/* Game Stats */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2 text-yellow-400">
            <Trophy className="w-5 h-5" />
            <span className="font-bold text-xl">Score: {score}</span>
          </div>
          <div className="flex items-center gap-2 text-blue-400">
            <Wind className="w-5 h-5" />
            <span className="font-bold text-xl">Attempts: {attempts}</span>
          </div>
          {streak > 2 && gameState === 'playing' && (
            <div className="flex items-center gap-2 text-orange-400 animate-pulse">
              <span className="text-2xl">🔥</span>
              <span className="font-bold text-lg">x{streak} Streak!</span>
            </div>
          )}
        </div>

        {/* Feedback */}
        {feedback && (
          <div className="text-center mb-2">
            <span className="text-2xl font-bold text-yellow-400 animate-bounce">
              {feedback}
            </span>
          </div>
        )}

        {/* Game Canvas */}
        <div className="relative bg-black rounded-lg overflow-hidden mb-4">
          <canvas
            ref={canvasRef}
            width={600}
            height={500}
            className="w-full h-auto"
          />
          
          {gameState === 'waiting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
              <Wind className="w-16 h-16 text-cyan-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Bottle Flip Challenge</h3>
              <p className="text-gray-300 text-center mb-4 max-w-md px-4">
                Flip the bottle and land it upright on the platform!
                Hold the button to charge power, release to flip. 15 attempts!
              </p>
              <Button onClick={startGame} className="gaming-button">
                Start Game
              </Button>
            </div>
          )}

          {gameState === 'finished' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90">
              <Trophy className="w-20 h-20 text-yellow-400 mb-4" />
              <h3 className="text-3xl font-bold text-white mb-2">Game Over!</h3>
              <p className="text-2xl text-yellow-400 font-bold mb-4">Final Score: {score}</p>
              <p className="text-gray-300 mb-6">Submitting your score...</p>
            </div>
          )}
        </div>

        {/* Flip Button */}
        <Button
          onMouseDown={handleFlipStart}
          onMouseUp={handleFlipRelease}
          onTouchStart={handleFlipStart}
          onTouchEnd={handleFlipRelease}
          disabled={gameState !== 'playing' || bottleRef.current.isFlying}
          className="gaming-button w-full h-20 text-2xl font-bold"
        >
          {isCharging ? `RELEASE! (${powerLevel}%)` : 'HOLD TO FLIP'}
        </Button>

        {/* Instructions */}
        <div className="mt-4 text-sm text-gray-400 text-center">
          <p>💧 Hold button to charge power, release to flip</p>
          <p>🎯 Land upright on the brown platform for points</p>
          <p>🔥 Build streaks for bonus points!</p>
        </div>
      </div>
    </div>
  );
};

export default BottleFlip;
